import { Button, ColorPicker, Container, Element, Label, SelectInput, SliderInput, TextInput, BooleanInput } from '@playcanvas/pcui';

import { Pose } from '../camera-poses';
import { localize } from './localization';
import { Events } from '../events';
import { ExportType, SceneExportOptions } from '../file-handler';
import { AnimTrack, ExperienceSettings } from '../splat-serialize';
import { buildViewerExportData, ViewerExportAnnotation, ViewerExportBookmark, ViewerExportCaseMetadata, ViewerExportEvidenceItem, ViewerExportTour } from '../viewer-export-extension';
import sceneExport from './svg/export.svg';

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

const removeKnownExtension = (filename: string) => {
    // remove known extensions (ordered from longest to shortest for compound extensions)
    const knownExtensions = [
        '.compressed.ply',
        '.ksplat',
        '.splat',
        '.html',
        '.ply',
        '.sog',
        '.spz',
        '.lcc',
        '.zip'
    ];

    for (let i = 0; i < knownExtensions.length; ++i) {
        const ext = knownExtensions[i];
        if (filename.endsWith(ext)) {
            return filename.slice(0, -ext.length);
        }
    }

    return filename;
};

class ExportPopup extends Container {
    show: (exportType: ExportType, splatNames: string[], showFilenameEdit: boolean) => Promise<null | SceneExportOptions>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, args = {}) {
        args = {
            id: 'export-popup',
            hidden: true,
            tabIndex: -1,
            ...args
        };

        super(args);

        // UI

        const dialog = new Container({
            id: 'dialog'
        });

        // header

        const header = new Container({
            id: 'header'
        });

        const headerText = new Label({
            id: 'header',
            text: localize('popup.export.header')
        });

        header.append(createSvg(sceneExport, {
            id: 'icon'
        }));

        header.append(headerText);

        // content

        const content = new Container({ id: 'content' });

        // type

        const viewerTypeRow = new Container({
            class: 'row'
        });

        const viewerTypeLabel = new Label({
            class: 'label',
            text: localize('popup.export.type')
        });

        const viewerTypeSelect = new SelectInput({
            class: 'select',
            defaultValue: 'html',
            options: [
                { v: 'html', t: localize('popup.export.html') },
                { v: 'zip', t: localize('popup.export.package') }
            ]
        });

        viewerTypeRow.append(viewerTypeLabel);
        viewerTypeRow.append(viewerTypeSelect);

        // viewer: camera start position

        const startRow = new Container({
            class: 'row'
        });

        const startLabel = new Label({
            class: 'label',
            text: localize('popup.export.start-position')
        });

        const startSelect = new SelectInput({
            class: 'select',
            defaultValue: 'viewport',
            options: [
                { v: 'default', t: localize('popup.export.default') },
                { v: 'viewport', t: localize('popup.export.viewport') },
                { v: 'pose', t: localize('popup.export.pose-camera') }
            ]
        });

        startRow.append(startLabel);
        startRow.append(startSelect);

        // viewer: animation

        const animationLabel = new Label({ class: 'label', text: localize('popup.export.animation') });
        const animationSelect = new SelectInput({
            class: 'select',
            defaultValue: 'none',
            options: [
                { v: 'none', t: localize('popup.export.animation.none') },
                { v: 'track', t: localize('popup.export.animation.track') }
            ]
        });
        const animationRow = new Container({ class: 'row' });
        animationRow.append(animationLabel);
        animationRow.append(animationSelect);

        // viewer: clear color

        const colorRow = new Container({
            class: 'row'
        });

        const colorLabel = new Label({
            class: 'label',
            text: localize('popup.export.background-color')
        });

        const colorPicker = new ColorPicker({
            class: 'color-picker',
            value: [1, 1, 1, 1]
        });

        colorRow.append(colorLabel);
        colorRow.append(colorPicker);

        // viewer: fov

        const fovRow = new Container({
            class: 'row'
        });

        const fovLabel = new Label({
            class: 'label',
            text: localize('popup.export.fov')
        });

        const fovSlider = new SliderInput({
            class: 'slider',
            min: 10,
            max: 120,
            precision: 0,
            value: 60
        });

        fovRow.append(fovLabel);
        fovRow.append(fovSlider);

        // viewer: include annotations

        const includeAnnotationsRow = new Container({
            class: 'row'
        });

        const includeAnnotationsLabel = new Label({
            class: 'label',
            text: 'Include annotations'
        });

        const includeAnnotationsBoolean = new BooleanInput({
            class: 'boolean',
            type: 'toggle'
        });

        includeAnnotationsRow.append(includeAnnotationsLabel);
        includeAnnotationsRow.append(includeAnnotationsBoolean);

        // viewer: include bookmarks

        const includeBookmarksRow = new Container({
            class: 'row'
        });

        const includeBookmarksLabel = new Label({
            class: 'label',
            text: 'Include bookmarks'
        });

        const includeBookmarksBoolean = new BooleanInput({
            class: 'boolean',
            type: 'toggle'
        });

        includeBookmarksRow.append(includeBookmarksLabel);
        includeBookmarksRow.append(includeBookmarksBoolean);

        // viewer: include tour

        const includeTourRow = new Container({
            class: 'row'
        });

        const includeTourLabel = new Label({
            class: 'label',
            text: 'Include tour'
        });

        const includeTourBoolean = new BooleanInput({
            class: 'boolean',
            type: 'toggle'
        });

        includeTourRow.append(includeTourLabel);
        includeTourRow.append(includeTourBoolean);

        // compress

        const compressRow = new Container({
            class: 'row'
        });

        const compressLabel = new Label({
            class: 'label',
            text: localize('popup.export.compress-ply')
        });

        const compressBoolean = new BooleanInput({
            class: 'boolean',
            type: 'toggle'
        });

        compressRow.append(compressLabel);
        compressRow.append(compressBoolean);

        // splats

        const splatsRow = new Container({
            class: 'row'
        });

        const splatsLabel = new Label({
            class: 'label',
            text: localize('popup.export.splats-select')
        });

        const splatsSelect = new SelectInput({
            class: 'select',
            defaultValue: 'ply',
            options: [
                { v: 'all', t: localize('popup.export.splats-select.all') }
            ]
        });

        splatsRow.append(splatsLabel);
        splatsRow.append(splatsSelect);

        // spherical harmonic bands

        const bandsRow = new Container({
            class: 'row'
        });

        const bandsLabel = new Label({
            class: 'label',
            text: localize('popup.export.sh-bands')
        });

        const bandsSlider = new SliderInput({
            class: 'slider',
            min: 0,
            max: 3,
            precision: 0,
            value: 3
        });

        bandsRow.append(bandsLabel);
        bandsRow.append(bandsSlider);

        // sog iterations

        const iterationsRow = new Container({
            class: 'row'
        });

        const iterationsLabel = new Label({
            class: 'label',
            text: localize('popup.export.iterations')
        });

        const iterationsSlider = new SliderInput({
            class: 'slider',
            min: 1,
            max: 20,
            precision: 0,
            value: 10
        });

        iterationsRow.append(iterationsLabel);
        iterationsRow.append(iterationsSlider);

        // filename

        const filenameRow = new Container({
            class: 'row'
        });

        const filenameLabel = new Label({
            class: 'label',
            text: localize('popup.export.filename')
        });

        const filenameEntry = new TextInput({
            class: 'text-input'
        });

        filenameRow.append(filenameLabel);
        filenameRow.append(filenameEntry);

        // content

        content.append(viewerTypeRow);
        content.append(startRow);
        content.append(animationRow);
        content.append(colorRow);
        content.append(fovRow);
        content.append(includeAnnotationsRow);
        content.append(includeBookmarksRow);
        content.append(includeTourRow);
        content.append(compressRow);
        content.append(splatsRow);
        content.append(bandsRow);
        content.append(iterationsRow);
        content.append(filenameRow);

        // footer

        const footer = new Container({ id: 'footer' });

        const cancelButton = new Button({
            class: 'button',
            text: localize('popup.cancel')
        });

        const exportButton = new Button({
            class: 'button',
            text: localize('popup.export')
        });

        footer.append(cancelButton);
        footer.append(exportButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        // handlers

        let onCancel: () => void;
        let onExport: () => void;

        cancelButton.on('click', () => onCancel());
        exportButton.on('click', () => onExport());

        const keydown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'Escape':
                    onCancel();
                    break;
                case 'Enter':
                    if (!e.shiftKey) onExport();
                    break;
                default:
                    e.stopPropagation();
                    break;
            }
        };

        const updateExtension = (ext: string) => {
            filenameEntry.value = removeKnownExtension(filenameEntry.value) + ext;
        };

        compressBoolean.on('change', () => {
            updateExtension(compressBoolean.value ? '.compressed.ply' : '.ply');
        });

        viewerTypeSelect.on('change', () => {
            updateExtension(viewerTypeSelect.value === 'html' ? '.html' : '.zip');
        });

        const reset = (exportType: ExportType, splatNames: string[], hasPoses: boolean) => {
            const allRows = [
                viewerTypeRow, startRow, animationRow, colorRow, fovRow, includeAnnotationsRow, includeBookmarksRow, includeTourRow, compressRow, splatsRow, bandsRow, iterationsRow, filenameRow
            ];

            const activeRows = {
                ply: [compressRow, splatsRow, bandsRow, filenameRow],
                splat: [splatsRow, filenameRow],
                sog: [splatsRow, bandsRow, iterationsRow, filenameRow],
                viewer: [viewerTypeRow, startRow, animationRow, colorRow, fovRow, includeAnnotationsRow, includeBookmarksRow, includeTourRow, splatsRow, bandsRow, filenameRow],
                bundle: [splatsRow, filenameRow]
            }[exportType];

            allRows.forEach((r) => {
                r.hidden = activeRows.indexOf(r) === -1;
            });

            // update splat list
            splatsSelect.options = [
                {
                    v: 'all',
                    t: localize('popup.export.splats-select.all')
                },
                ...splatNames.map((s, i) => ({ v: i.toFixed(0), t: s }))
            ];
            splatsSelect.value = 'all';
            splatsSelect.enabled = splatNames.length > 1;

            bandsSlider.value = events.invoke('view.bands');

            // ply
            compressBoolean.value = false;

            // sog
            iterationsSlider.value = 10;

            // filename
            filenameEntry.value = splatNames[0];
            switch (exportType) {
                case 'ply':
                    updateExtension('.ply');
                    break;
                case 'splat':
                    updateExtension('.splat');
                    break;
                case 'sog':
                    updateExtension('.sog');
                    break;
                case 'viewer':
                    updateExtension(viewerTypeSelect.value === 'html' ? '.html' : '.zip');
                    break;
                case 'bundle':
                    updateExtension('.nsite');
                    break;
            }

            // viewer
            const bgClr = events.invoke('bgClr');

            includeAnnotationsBoolean.value = true;
            includeBookmarksBoolean.value = true;
            includeTourBoolean.value = true;

            startSelect.value = hasPoses ? 'pose' : 'viewport';
            startSelect.disabledOptions = hasPoses ? {} : { 'pose': startSelect.options[2].t };

            animationSelect.value = hasPoses ? 'track' : 'none';
            animationSelect.disabledOptions = hasPoses ? { } : { track: animationSelect.options[1].t };
            animationSelect.enabled = hasPoses;

            colorPicker.value = [bgClr.r, bgClr.g, bgClr.b];

            fovSlider.value = events.invoke('camera.fov');
        };

        this.show = (exportType: ExportType, splatNames: string[], showFilenameEdit: boolean) => {
            const frames = events.invoke('timeline.frames');
            const frameRate = events.invoke('timeline.frameRate');
            const smoothness = events.invoke('timeline.smoothness');
            const orderedPoses = (events.invoke('camera.poses') as Pose[])
            .slice()
            .filter(p => p.frame >= 0 && p.frame < frames)
            .sort((a, b) => a.frame - b.frame);

            reset(exportType, splatNames, orderedPoses.length > 0);

            // filename is only shown in safari where file picker is not supported
            filenameRow.hidden = !showFilenameEdit;

            this.hidden = false;
            this.dom.addEventListener('keydown', keydown);
            this.dom.focus();

            const assemblePlyOptions = () : SceneExportOptions => {
                return {
                    filename: filenameEntry.value,
                    splatIdx: splatsSelect.value === 'all' ? 'all' : splatsSelect.value,
                    serializeSettings: {
                        maxSHBands: bandsSlider.value
                    },
                    compressedPly: compressBoolean.value
                };
            };

            const assembleSplatOptions = () : SceneExportOptions => {
                return {
                    filename: filenameEntry.value,
                    splatIdx: splatsSelect.value === 'all' ? 'all' : splatsSelect.value,
                    serializeSettings: { }
                };
            };

            const assembleSogOptions = () : SceneExportOptions => {
                return {
                    filename: filenameEntry.value,
                    splatIdx: splatsSelect.value === 'all' ? 'all' : splatsSelect.value,
                    serializeSettings: {
                        maxSHBands: bandsSlider.value
                    },
                    sogIterations: iterationsSlider.value
                };
            };

            const assembleViewerOptions = () : SceneExportOptions => {
                // extract camera starting pos
                let pose;
                switch (startSelect.value) {
                    case 'pose':
                        pose = orderedPoses?.[0];
                        break;
                    case 'viewport':
                        pose = events.invoke('camera.getPose');
                        break;
                }
                const p = pose?.position;
                const t = pose?.target;

                const startAnim = (() => {
                    switch (animationSelect.value) {
                        case 'none': return 'none';
                        case 'track': return 'animTrack';
                    }
                })();

                // extract camera animation
                const animTracks: AnimTrack[] = [];
                switch (startAnim) {
                    case 'none':
                        break;
                    case 'animTrack': {
                        // use camera poses
                        const times = [];
                        const position = [];
                        const target = [];
                        for (let i = 0; i < orderedPoses.length; ++i) {
                            const p = orderedPoses[i];
                            times.push(p.frame);
                            position.push(p.position.x, p.position.y, p.position.z);
                            target.push(p.target.x, p.target.y, p.target.z);
                        }

                        animTracks.push({
                            name: 'cameraAnim',
                            duration: frames / frameRate,
                            frameRate,
                            target: 'camera',
                            loopMode: 'repeat',
                            interpolation: 'spline',
                            smoothness,
                            keyframes: {
                                times,
                                values: { position, target }
                            }
                        });

                        break;
                    }
                }

                const experienceSettings: ExperienceSettings = {
                    camera: {
                        fov: fovSlider.value,
                        position: p ? [p.x, p.y, p.z] : null,
                        target: t ? [t.x, t.y, t.z] : null,
                        startAnim,
                        animTrack: startAnim === 'animTrack' ? 'cameraAnim' : null
                    },
                    background: {
                        color: colorPicker.value.slice()
                    },
                    animTracks
                };

                const annotationLayers = (events.invoke('annotations.layers') as Array<{ id: string; visible: boolean }> | undefined) ?? [];
                const visibleLayerIds = annotationLayers
                .filter(layer => layer?.visible)
                .map(layer => String(layer.id));

                const annotations = ((events.invoke('annotations.list') as ViewerExportAnnotation[] | undefined) ?? [])
                .map(annotation => ({
                    ...annotation,
                    worldPosition: annotation.worldPosition ? annotation.worldPosition.slice() as [number, number, number] : annotation.worldPosition,
                    worldNormal: annotation.worldNormal ? annotation.worldNormal.slice() as [number, number, number] : undefined,
                    linkedEvidenceIds: annotation.linkedEvidenceIds?.slice()
                }));

                const bookmarks = ((events.invoke('bookmarks.list') as ViewerExportBookmark[] | undefined) ?? [])
                .map(bookmark => ({
                    ...bookmark,
                    cameraPose: {
                        ...bookmark.cameraPose,
                        position: bookmark.cameraPose.position.slice() as [number, number, number],
                        target: bookmark.cameraPose.target.slice() as [number, number, number]
                    }
                }));

                const tours = ((events.invoke('tours.list') as ViewerExportTour[] | undefined) ?? [])
                .map(tour => ({
                    ...tour,
                    steps: (tour.steps ?? []).map(step => ({
                        ...step,
                        cameraPose: step.cameraPose ? {
                            ...step.cameraPose,
                            position: step.cameraPose.position.slice() as [number, number, number],
                            target: step.cameraPose.target.slice() as [number, number, number]
                        } : undefined
                    }))
                }));

                const evidenceItems = ((events.invoke('pictures.list') as Array<any> | undefined) ?? [])
                .map((picture): ViewerExportEvidenceItem => ({
                    id: String(picture.id),
                    name: picture.name,
                    frame: picture.frame,
                    exhibitId: picture.exhibitId,
                    capturedAt: picture.capturedAt,
                    capturedBy: picture.capturedBy,
                    device: picture.device,
                    tags: Array.isArray(picture.tags) ? picture.tags.slice() : undefined,
                    description: picture.description,
                    position: picture.position ? [picture.position.x, picture.position.y, picture.position.z] : undefined,
                    target: picture.target ? [picture.target.x, picture.target.y, picture.target.z] : undefined,
                    image: picture.image ? { filename: picture.image.filename } : undefined
                }));

                const caseMetadata = ((events.invoke('case.metadata') as ViewerExportCaseMetadata | undefined) ?? undefined);

                const embeddedData = buildViewerExportData({
                    include: {
                        annotations: !!includeAnnotationsBoolean.value,
                        bookmarks: !!includeBookmarksBoolean.value,
                        tours: !!includeTourBoolean.value
                    },
                    annotations,
                    annotationVisibleLayerIds: visibleLayerIds,
                    bookmarks,
                    tours,
                    evidenceItems,
                    caseMetadata
                });

                return {
                    filename: filenameEntry.value,
                    splatIdx: splatsSelect.value === 'all' ? 'all' : splatsSelect.value,
                    serializeSettings: {
                        maxSHBands: bandsSlider.value
                    },
                    viewerExportSettings: {
                        type: viewerTypeSelect.value,
                        experienceSettings,
                        embeddedData
                    }
                };
            };

            return new Promise<null | SceneExportOptions>((resolve) => {
                onCancel = () => {
                    resolve(null);
                };

                onExport = () => {
                    switch (exportType) {
                        case 'ply':
                            resolve(assemblePlyOptions());
                            break;
                        case 'splat':
                            resolve(assembleSplatOptions());
                            break;
                        case 'sog':
                            resolve(assembleSogOptions());
                            break;
                        case 'viewer':
                            resolve(assembleViewerOptions());
                            break;
                    }
                };
            }).finally(() => {
                this.dom.removeEventListener('keydown', keydown);
                this.hide();
            });
        };

        this.hide = () => {
            this.hidden = true;
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { ExportPopup };
